import { useEffect, useState } from "react";
import { useTranslation } from "@i18n";
import { Box } from "@mui/material";
import { latestClosedMonth, localDateToMonthKey, monthKeyToLocalDate } from "@Helpers";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import DatePickerComponent from "@CubeComponents/Mui_Form/DatePickerComponent";
import Text from "@CubeComponents/html/Text/Text";
import type { SlaReportPortalOption } from "./slaReportModel";

export type SlaExportSelection = {
  month: string;
  /**
   * ⚠️ WHITELISTED CALLERS ONLY, and its ABSENCE is meaningful rather than a missing default: it is
   * what tells the server "my own portal". A VIEW_SUPPORT_SLA user never gets a picker and never
   * sends this, so their report is pinned to the portal they are signed in to and the deployment
   * that portal runs on — the server re-derives both and ignores anything sent, so the pinning does
   * not depend on this component behaving.
   */
  portal?: SlaReportPortalOption;
};

type PickerOption = { value: string; label: string };

const SlaExportModal = ({
  isVisible,
  isWhiteListedUser,
  portals,
  exporting,
  onCancel,
  onExport,
}: {
  isVisible: boolean;
  isWhiteListedUser: boolean;
  /** Every portal, for the whitelisted picker. Empty for everyone else, who get no picker. */
  portals: SlaReportPortalOption[];
  exporting: boolean;
  onCancel: () => void;
  onExport: (selection: SlaExportSelection) => void;
}) => {
  const { t } = useTranslation();

  // ⚠️ THE PARENT MOUNTS THIS ONLY WHILE IT IS OPEN, which is what makes every initialiser below
  // both the initial value AND the reset-on-reopen. Keeping it mounted and re-seeding from an
  // effect on `isVisible` is the "adjust state when a prop changes" anti-pattern
  // (react-effect/no-adjust-state-on-prop-change), and it also meant the clock was read on every
  // render of a closed dialog — an impure call during render (react-hooks/purity) whose answer
  // changes at midnight on the 1st. Reading it once per opening is the only reading needed: a month
  // cannot finish elapsing while the dialog is open.
  const [newestMonth] = useState(() => latestClosedMonth(Date.now()));
  // Defaults to the most recently completed month — the one a person opening this almost always
  // wants, and the newest the picker will accept.
  const [month, setMonth] = useState<string>(() => newestMonth);

  const portalOptions: PickerOption[] = portals.map((entry) => ({
    value: entry.frontendUrl,
    label: entry.label,
  }));
  const [portalValue, setPortalValue] = useState<string>("");
  const solePortalUrl = portals.length === 1 ? portals[0]?.frontendUrl : undefined;
  useEffect(() => {
    if (solePortalUrl) setPortalValue(solePortalUrl);
  }, [solePortalUrl]);
  const portal = portals.find((entry) => entry.frontendUrl === portalValue);

  const monthDate = monthKeyToLocalDate(month);
  const maxDate = monthKeyToLocalDate(newestMonth);
  // A whitelisted caller has no single portal for the server to fall back to, so exporting without
  // one chosen would widen the report rather than scope it. Everyone else is pinned server-side.
  const canExport = Boolean(month) && (!isWhiteListedUser || Boolean(portal)) && !exporting;

  const fieldLabel = (text: string) => <div className="mb-1.5 text-sm font-semibold text-primary_text">{text}</div>;

  const modalContent = () => (
    <Box className="flex flex-col gap-5 py-2" style={{ minWidth: "26rem" }}>
      {/* No picker for a VIEW_SUPPORT_SLA user: their report is their own portal's, full stop. */}
      {isWhiteListedUser && (
        <div>
          {fieldLabel(t("Portal"))}
          {/* `renderOption` is what suppresses the per-option tooltip: SelectDropDown wraps its
              DEFAULT option label in a ToolTipComponent and skips that branch entirely when the
              caller renders the option itself. The tooltip only ever repeated the visible label. */}
          <SelectDropDown<PickerOption>
            options={portalOptions}
            fields={{ text: "label", value: "value" }}
            value={portalValue || null}
            width="100%"
            disabled={Boolean(solePortalUrl)}
            placeholder={t("Select a portal")}
            onChange={(selected) => setPortalValue(selected && !Array.isArray(selected) ? selected.value : "")}
            renderOption={(option) => <span className="block truncate">{option.label}</span>}
          />
        </div>
      )}

      <div>
        {fieldLabel(t("Reporting month"))}
        {/* `views` without "day" makes this a month/year selector: it opens on the month grid and
            commits as soon as a month is picked. Any past month can be chosen — how far back a
            portal's tickets or availability files go is not this picker's business, and a month with
            neither is exported saying so. The only bound is `max`, the most recent COMPLETED month:
            a part-elapsed month would print a period it does not cover. */}
        <DatePickerComponent
          views={["year", "month"]}
          openTo="month"
          format="MMMM yyyy"
          value={monthDate}
          {...(maxDate ? { max: maxDate } : {})}
          width="100%"
          onChange={({ value }) => {
            const key = value ? localDateToMonthKey(value) : null;
            if (key) setMonth(key);
          }}
        />
      </div>
    </Box>
  );

  const footerContent = () => (
    <Box className="flex items-center justify-end gap-3">
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onCancel} disabled={exporting}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={!canExport}
        loading={exporting}
        onClick={() => onExport({ month, ...(isWhiteListedUser && portal ? { portal } : {}) })}
      >
        <Text>{t("Export")}</Text>
      </ButtonComponent>
    </Box>
  );

  return (
    <ModalStateDialogComponent
      isVisible={isVisible}
      onCancel={onCancel}
      headerTitle={t("Export SLA Report")}
      modelContent={modalContent()}
      footerContent={footerContent()}
    />
  );
};

export default SlaExportModal;
